import { Loader } from '@southfields-digital/mpxlive-components';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { StateType } from 'src/redux/reducers';
import {
  REQUEST_CREATE_MATCH_CONTROL_CLOCK,
  REQUEST_CREATE_MATCH_CONTROL_EVENT,
  REQUEST_DELETE_MATCH_CONTROL_CLOCK,
  REQUEST_DELETE_MATCH_CONTROL_EVENT,
  REQUEST_GET_MATCH_CONTROL,
  REQUEST_GET_MATCH_CONTROL_CONTROLS,
  REQUEST_START_MATCH_CONTROL_CLOCK,
  REQUEST_STOP_MATCH_CONTROL_CLOCK,
  REQUEST_UPDATE_MATCH_CONTROL,
  REQUEST_UPDATE_MATCH_CONTROL_CLOCK,
  REQUEST_UPDATE_MATCH_CONTROL_CONTESTANT,
  REQUEST_UPDATE_MATCH_CONTROL_GRAPHICS,
  REQUEST_UPDATE_MATCH_CONTROL_SHOOT_OUT,
} from 'src/redux/reducers/matchControls';
import {
  HockeyMatchControl,
  SoccerMatchControl,
  TennisMatchControl,
  VolleyballMatchControl,
  RugbyMatchControl,
} from 'src/types/matchControl';
import { CardTypes } from 'src/types/matchControl/card';
import { Clock } from 'src/types/matchControl/clock';
import { Contestant } from 'src/types/matchControl/contestant';
import { Control } from 'src/types/matchControl/control';
import { ShootOut } from 'src/types/matchControl/shootOut';

import Hockey from './sports/Hockey/Hockey';
import Rugby from './sports/Rugby';
import Soccer from './sports/Soccer/Soccer';
import Tennis from './sports/Tennis';
import Volleyball from './sports/Volleyball';
import { MatchControlVariant } from './types';
import { getCardTotalContestant } from './utils';

type Props = {
  variant?: MatchControlVariant;
  rundownId: string;
};

const MatchControl = ({ rundownId, variant }: Props) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    dispatch({
      type: REQUEST_GET_MATCH_CONTROL,
      payload: {
        rundownId,
        callback: () => setLoading(false),
      },
    });
    dispatch({ type: REQUEST_GET_MATCH_CONTROL_CONTROLS, payload: { rundownId } });
  }, [rundownId, dispatch]);

  const { data: matchControl } = useSelector(
    (state: StateType) => state.matchControls.matchControl
  );
  const { data: matchControlGraphics } = useSelector(
    (state: StateType) => state.matchControls.matchControlGraphics
  );

  const handleCreateMatchControlClock = (type: Clock['type'], contestantId?: Contestant['id']) => {
    if (!matchControl) return;

    dispatch({
      type: REQUEST_CREATE_MATCH_CONTROL_CLOCK,
      payload: {
        matchControlId: matchControl.id,
        contestantId,
        type,
      },
    });
  };

  const handleDeleteMatchControlClock = (clockId: Clock['id']) => {
    if (!matchControl) return;

    dispatch({
      type: REQUEST_DELETE_MATCH_CONTROL_CLOCK,
      payload: {
        matchControlId: matchControl.id,
        clockId,
      },
    });
  };

  const updateTennisMatchControl = (matchControl: TennisMatchControl) => {
    dispatch({
      type: REQUEST_UPDATE_MATCH_CONTROL,
      payload: {
        matchControlId: matchControl.id,
        config: { score: matchControl.score, server: matchControl.server },
      },
    });
  };

  const updateVolleyballMatchControl = (matchControl: VolleyballMatchControl) => {
    dispatch({
      type: REQUEST_UPDATE_MATCH_CONTROL,
      payload: {
        matchControlId: matchControl.id,
        config: { score: matchControl.score, server: matchControl.server },
      },
    });
  };

  const updateSoccerMatchControl = (matchControl: SoccerMatchControl) => {
    dispatch({
      type: REQUEST_UPDATE_MATCH_CONTROL,
      payload: {
        matchControlId: matchControl.id,
        config: { hasShootOut: matchControl.hasShootOut, shootOut: matchControl.shootOut },
      },
    });
  };

  const updateHockeyMatchControl = (matchControl: HockeyMatchControl) => {
    dispatch({
      type: REQUEST_UPDATE_MATCH_CONTROL,
      payload: {
        matchControlId: matchControl.id,
        config: {
          hasShootOut: matchControl.hasShootOut,
          shootOut: matchControl.shootOut,
          period: matchControl.period,
        },
      },
    });
  };

  const updateRugbyMatchControl = (matchControl: RugbyMatchControl) => {
    dispatch({
      type: REQUEST_UPDATE_MATCH_CONTROL,
      payload: {
        matchControlId: matchControl.id,
        config: {
          period: matchControl.period,
        },
      },
    });
  };

  const updateShootOutMatchControl = (
    matchControl: SoccerMatchControl | HockeyMatchControl,
    shootOut: ShootOut
  ) => {
    dispatch({
      type: REQUEST_UPDATE_MATCH_CONTROL_SHOOT_OUT,
      payload: {
        matchControlId: matchControl.id,
        shootOut,
      },
    });
  };

  const updateContestant = (contestant: Contestant) => {
    if (!matchControl) return;

    dispatch({
      type: REQUEST_UPDATE_MATCH_CONTROL_CONTESTANT,
      payload: { matchControlId: matchControl.id, contestant },
    });
  };

  const updateClock = (clock: Clock) => {
    if (!matchControl) return;

    dispatch({
      type: REQUEST_UPDATE_MATCH_CONTROL_CLOCK,
      payload: { matchControlId: matchControl.id, clock },
    });
  };

  const handleChangeExtraTime = (clock: Clock, amount: number) => {
    if (!('extraTime' in clock)) return;

    updateClock({ ...clock, extraTime: Math.max(0, amount) });
  };

  const handleChangeTime = (clock: Clock, time: number) => {
    updateClock({ ...clock, clockTime: time });
  };

  const handleChangeScore = (contestant: Contestant, goals: number) => {
    if (!('points' in contestant.score)) return;

    if (contestant.score.points < goals) {
      handleAddGoal(contestant);
    } else {
      handleDeleteGoal(contestant);
    }
  };

  const handleAddGoal = (contestant: Contestant) => {
    dispatch({
      type: REQUEST_CREATE_MATCH_CONTROL_EVENT,
      payload: {
        matchControlId: matchControl?.id,
        event: { type: 'goal', entityId: contestant.id, entityType: 'contestant' },
      },
    });
  };

  const handleDeleteGoal = (contestant: Contestant) => {
    dispatch({
      type: REQUEST_DELETE_MATCH_CONTROL_EVENT,
      payload: {
        matchControlId: matchControl?.id,
        event: { type: 'goal', entityId: contestant.id, entityType: 'contestant' },
      },
    });
  };

  const handleChangeShortName = (contestant: Contestant, name: string) => {
    updateContestant({ ...contestant, shortName: name });
  };

  const handleChangeColor = (
    contestant: Contestant,
    color: 'primaryColor' | 'secondaryColor',
    value: string
  ) => {
    updateContestant({ ...contestant, [color]: value });
  };

  const handleChangeCards = (contestant: Contestant, card: CardTypes, amount: number) => {
    const currentAmount = getCardTotalContestant(card, contestant);
    if (currentAmount < amount) {
      handleAddCard(contestant, card);
    } else {
      handleDeleteCard(contestant, card);
    }
  };

  const handleVideoReferral = (contestant: Contestant, value: boolean) => {
    const eventType = value
      ? REQUEST_CREATE_MATCH_CONTROL_EVENT
      : REQUEST_DELETE_MATCH_CONTROL_EVENT;

    dispatch({
      type: eventType,
      payload: {
        matchControlId: matchControl?.id,
        event: { type: 'video_referral', entityId: contestant.id, entityType: 'contestant' },
      },
    });
  };

  const handleAddCard = (contestant: Contestant, type: CardTypes) => {
    dispatch({
      type: REQUEST_CREATE_MATCH_CONTROL_EVENT,
      payload: {
        matchControlId: matchControl?.id,
        event: { type, entityId: contestant.id, entityType: 'contestant' },
      },
    });
  };

  const handleDeleteCard = (contestant: Contestant, type: CardTypes) => {
    dispatch({
      type: REQUEST_DELETE_MATCH_CONTROL_EVENT,
      payload: {
        matchControlId: matchControl?.id,
        event: { type, entityId: contestant.id, entityType: 'contestant' },
      },
    });
  };

  const handleAddRugbyScoringEvent = (contestant: Contestant, eventType: string) => {
    dispatch({
      type: REQUEST_CREATE_MATCH_CONTROL_EVENT,
      payload: {
        matchControlId: matchControl?.id,
        event: { type: eventType, entityId: contestant.id, entityType: 'contestant' },
      },
    });
  };

  const handleRemoveRugbyScoringEvent = (contestant: Contestant, eventType: string) => {
    dispatch({
      type: REQUEST_DELETE_MATCH_CONTROL_EVENT,
      payload: {
        matchControlId: matchControl?.id,
        event: { type: eventType, entityId: contestant.id, entityType: 'contestant' },
      },
    });
  };

  const handleStartTime = (clock: Clock) => {
    dispatch({
      type: REQUEST_START_MATCH_CONTROL_CLOCK,
      payload: { clock },
    });
  };

  const handleStopTime = (clock: Clock) => {
    dispatch({
      type: REQUEST_STOP_MATCH_CONTROL_CLOCK,
      payload: { clock },
    });
  };

  const handleDispatchControl = (control?: Control) => {
    if (!control) return;
    dispatch({ type: REQUEST_UPDATE_MATCH_CONTROL_GRAPHICS, payload: { rundownId, control } });
  };

  if (loading) {
    return (
      <div className="grid place-items-center h-full [&>div]:transform-none">
        <Loader />
      </div>
    );
  }

  if (!matchControl) return null;

  switch (matchControl.sport) {
    case 'soccer':
      return (
        <Soccer
          key={matchControl.id}
          matchControl={matchControl}
          handleChangeCards={handleChangeCards}
          handleChangeColor={handleChangeColor}
          handleChangeExtraTime={handleChangeExtraTime}
          handleChangeScore={handleChangeScore}
          handleChangeShortName={handleChangeShortName}
          handleChangeTime={handleChangeTime}
          handleDispatchControl={handleDispatchControl}
          handleStartTime={handleStartTime}
          handleStopTime={handleStopTime}
          matchControlGraphics={matchControlGraphics || []}
          handleUpdateMatchControl={(matchControl) => updateSoccerMatchControl(matchControl)}
          handleUpdateShootOut={(shootOut) => updateShootOutMatchControl(matchControl, shootOut)}
          variant={variant}
          handleCreateClock={handleCreateMatchControlClock}
          handleDeleteClock={handleDeleteMatchControlClock}
        />
      );
    case 'hockey':
      return (
        <Hockey
          key={matchControl.id}
          matchControl={matchControl}
          handleChangeShortName={handleChangeShortName}
          handleChangeColor={handleChangeColor}
          handleChangeScore={handleChangeScore}
          handleChangeCards={handleChangeCards}
          handleVideoReferral={handleVideoReferral}
          handleChangeTime={handleChangeTime}
          handleDispatchControl={handleDispatchControl}
          handleStartTime={handleStartTime}
          handleStopTime={handleStopTime}
          matchControlGraphics={matchControlGraphics || []}
          handleUpdateMatchControl={(matchControl) => updateHockeyMatchControl(matchControl)}
          handleUpdateShootOut={(shootOut) => updateShootOutMatchControl(matchControl, shootOut)}
          handleCreateClock={handleCreateMatchControlClock}
          handleDeleteClock={handleDeleteMatchControlClock}
          variant={variant}
        />
      );
    case 'tennis':
      return (
        <Tennis
          key={matchControl.id}
          contestant1={matchControl.contestants[0]}
          contestant2={matchControl.contestants[1]}
          matchControl={matchControl}
          handleUpdateMatchControl={(matchControl) => updateTennisMatchControl(matchControl)}
          handleDispatchControl={handleDispatchControl}
          matchControlGraphics={matchControlGraphics || []}
        />
      );
    case 'volleyball':
      return (
        <Volleyball
          key={matchControl.id}
          contestant1={matchControl.contestants[0]}
          contestant2={matchControl.contestants[1]}
          matchControl={matchControl}
          handleUpdateMatchControl={(matchControl) => updateVolleyballMatchControl(matchControl)}
          handleDispatchControl={handleDispatchControl}
          matchControlGraphics={matchControlGraphics || []}
        />
      );
    case 'rugby':
      return (
        <Rugby
          key={matchControl.id}
          matchControl={matchControl}
          handleChangeShortName={handleChangeShortName}
          handleChangeColor={handleChangeColor}
          handleChangeTime={handleChangeTime}
          handleDispatchControl={handleDispatchControl}
          handleStartTime={handleStartTime}
          handleStopTime={handleStopTime}
          matchControlGraphics={matchControlGraphics || []}
          handleUpdateMatchControl={(matchControl) => updateRugbyMatchControl(matchControl)}
          handleAddScoringEvent={handleAddRugbyScoringEvent}
          handleRemoveScoringEvent={handleRemoveRugbyScoringEvent}
          variant={variant}
        />
      );
    default:
      return (
        <div className="pt-2 text-center">This sport has no match control implemented yet</div>
      );
  }
};

export default MatchControl;
